'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Flex } from '@/components/layout/flex';
import { mapQueryBy, mapSortBy, titlePhotoPath, truncate } from 'sdk';
import { Title, Series } from 'types';
import Text from '@/components/typography/text';
import Input from '@/components/form/input';
import Button from '@/components/button';
import { search } from 'sdk';
import styles from './bundle-search-facet.module.scss';
import LoadableImage from '@/components/loadable-image';
import { images } from 'sdk';
import PopularSeries from './bundle-popular-series';
import PlusSVG from '~/assets/svg/icons/plus';
import COLORS from '~/lib/helpers/color-helper';
import Card from '~/components/card';
import Heading from '@/components/typography/heading';
import { Box } from '~/components/layout/box';
import Trash from '~/assets/svg/icons/trash';

interface TitleSuggestion {
  id: string;
  title: string;
  author: string;
  title_id: string;
  photos: string[];
}

interface BundleSearchFacetsProps {
  series: Series[];
  initialTitles?: Title[];
}

export default function BundleSearchFacets({
  series,
  initialTitles = [],
}: BundleSearchFacetsProps) {
  const [books, setBooks] = useState<string[]>(() => {
    if (initialTitles.length > 0) {
      return initialTitles.map((title) => title?.title);
    }
    return ['', ''];
  });

  const [selectedTitleIds, setSelectedTitleIds] = useState<string[]>(() => {
    if (initialTitles.length > 0) {
      return initialTitles.map((title) => title?.id);
    }
    return ['', ''];
  });

  const [suggestions, setSuggestions] = useState<TitleSuggestion[][]>(() =>
    Array(Math.max(initialTitles.length, 2)).fill([])
  );

  useEffect(() => {
    if (initialTitles.length > 0) {
      setBooks(initialTitles.map((title) => title?.title));
      setSelectedTitleIds(initialTitles.map((title) => title?.id));
      setSuggestions(Array(initialTitles.length).fill([]));
    }
  }, [initialTitles]);

  const [selectedSeries, setSelectedSeries] = useState<Series>(series[0]);
  const [highlightedIndex, setHighlightedIndex] = useState<number[]>([-1, -1]);
  const router = useRouter();

  const handleBookChange = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const newBooks = [...books];
    newBooks[index] = value || '';
    setBooks(newBooks);

    if (value.trim().length >= 3) {
      try {
        const result = await search({
          index: 'titles',
          params: {
            q: value,
            query_by: mapQueryBy('titles'),
            per_page: 5,
            sort_by: mapSortBy('titles'),
            filter_by: 'copies:>0',
          },
          options: {},
        });

        const newSuggestions = [...suggestions];
        newSuggestions[index] = result.hits.map((hit) => ({
          id: hit.document.id,
          title: hit.document.title,
          author: hit.document.authors,
          title_id: hit.document.title_id || hit.document.id,
          photos: hit.document.photos,
        }));
        setSuggestions(newSuggestions);
      } catch (error) {
        console.error('Search error:', error);
      }
    } else {
      const newSuggestions = [...suggestions];
      newSuggestions[index] = [];
      setSuggestions(newSuggestions);
    }

    setHighlightedIndex((prev) => {
      const newIndices = [...prev];
      newIndices[index] = -1; // Reset highlighted index when input changes
      return newIndices;
    });
  };

  const handleSuggestionSelect = (
    index: number,
    suggestion: TitleSuggestion
  ) => {
    // Ensure title is a string
    const title = typeof suggestion.title === 'string' ? suggestion.title : '';
    const newBooks = [...books];
    newBooks[index] = title;
    setBooks(newBooks);

    // Ensure title_id is a string
    const titleId =
      typeof suggestion.title_id === 'string' ? suggestion.title_id : '';
    const newTitleIds = Array.isArray(selectedTitleIds)
      ? [...selectedTitleIds]
      : ['', ''];
    newTitleIds[index] = titleId;
    setSelectedTitleIds(newTitleIds);

    const newSuggestions = [...suggestions];
    newSuggestions[index] = [];
    setSuggestions(newSuggestions);
  };

  const handleSearch = () => {
    const validTitleIds = Array.isArray(selectedTitleIds)
      ? selectedTitleIds.filter((id) => id && id.trim() !== '')
      : [];

    if (validTitleIds.length === 0) {
      alert('Please select at least 1 book.');
      return;
    } else if (validTitleIds.length === 1) {
      alert('Please select at least 2 books.');
      return;
    }

    const queryString = validTitleIds.join(',');

    if (!queryString) {
      return;
    }

    updateQueryParams(queryString);
  };

  const updateQueryParams = (titleIds: string) => {
    if (!titleIds) {
      return;
    }

    const encodedTitleIds = encodeURIComponent(titleIds);
    const url = `/bundle-search?title_ids=${encodedTitleIds}`;
    router.push(url);
  };

  const addInputBox = () => {
    setBooks([...books, '']);
    setSuggestions([...suggestions, []]);
    setSelectedTitleIds([...selectedTitleIds, '']);
  };

  const removeInputBox = (indexToRemove: number) => {
    if (books.length > 2) {
      setBooks(books.filter((_, index) => index !== indexToRemove));
      setSuggestions(suggestions.filter((_, index) => index !== indexToRemove));
      setSelectedTitleIds(
        selectedTitleIds.filter((_, index) => index !== indexToRemove)
      );
    }
  };

  const handleSelectSeries = (series: Series) => {
    if (series?.seriesTitle === 'Select Series to Search') {
      setSelectedTitleIds(['']);
      setBooks(['']);
      setSuggestions([[]]);
      return;
    }

    const idsArray = series.titles.map((t) => t.id);
    const titlesArray = series.titles.map((t) => t.title);
    setSelectedTitleIds(idsArray);
    setBooks(titlesArray);
    setSuggestions(titlesArray.map(() => [])); // Reset suggestions
    setSelectedSeries(series);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    const suggestionsList = suggestions[index];
    if (event.key === 'ArrowDown') {
      setHighlightedIndex((prev) => {
        const newIndices = [...prev];
        newIndices[index] = (prev[index] + 1) % suggestionsList.length;
        return newIndices;
      });
    } else if (event.key === 'ArrowUp') {
      setHighlightedIndex((prev) => {
        const newIndices = [...prev];
        newIndices[index] =
          (prev[index] - 1 + suggestionsList.length) % suggestionsList.length;
        return newIndices;
      });
    } else if (event.key === 'Enter' && highlightedIndex[index] !== -1) {
      handleSuggestionSelect(index, suggestionsList[highlightedIndex[index]]);
    } else if (event.key === 'Escape') {
      const newBooks = [...books];
      newBooks[index] = '';
      setBooks(newBooks);

      const newSuggestions = [...suggestions];
      newSuggestions[index] = [];
      setSuggestions(newSuggestions);
    }
  };

  const handleClear = () => {
    setBooks(['', '']); // Reset to initial two empty inputs
    setSuggestions([[], []]); // Reset suggestions
    setSelectedTitleIds(['', '']); // Reset selected title IDs
    setSelectedSeries(series[0]); // Reset selected series to default
  };

  function renderInputs() {
    return books.map((book, index) => {
      const showSuggestions = suggestions[index].length > 0;
      const showCloseButton = books.length > 2;

      return (
        <Flex
          key={index}
          style={{
            position: 'relative',
            width: '100%',
          }}
        >
          <Flex
            direction="row"
            align="center"
            gap={10}
            style={{ width: '100%' }}
          >
            <Box className={styles.inputBox} style={{ width: '100%' }}>
              <Input
                type="text"
                id={`book-${index + 1}`}
                value={book}
                onChange={(e) => handleBookChange(index, e)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                placeholder={`Enter book ${index + 1} title`}
              />
            </Box>
            {showCloseButton && (
              <Flex
                align="center"
                justify="center"
                onClick={() => removeInputBox(index)}
                className={styles.iconButton}
              >
                <Trash fillColor={COLORS.primary} />
              </Flex>
            )}
          </Flex>
          {showSuggestions && (
            <Card mt={0.5} className={styles.suggestions}>
              {suggestions[index].map((suggestion, suggestionIndex) => {
                const isHighlighted =
                  highlightedIndex[index] === suggestionIndex;
                return (
                  <div
                    key={suggestion.id}
                    className={`${styles.suggestionItem} ${
                      isHighlighted ? styles.highlighted : ''
                    }`}
                    onClick={() => handleSuggestionSelect(index, suggestion)}
                  >
                    <div className={styles['suggestion-item-image']}>
                      {suggestion.photos && (
                        <LoadableImage
                          key={suggestion?.id}
                          width={50}
                          height={50}
                          src={
                            images(
                              titlePhotoPath(suggestion as unknown as Title)
                            ).book.tiny
                          }
                          alt={suggestion.title}
                        />
                      )}
                    </div>
                    <div className={styles['suggestion-info']}>
                      <Heading size="base">
                        {truncate(suggestion.title, 50)}
                      </Heading>
                      <Text size="small" color="copy">
                        {truncate(
                          Array.isArray(suggestion.author)
                            ? suggestion.author.map((a) => a.name).join(', ')
                            : suggestion.author,
                          50
                        )}
                      </Text>
                    </div>
                  </div>
                );
              })}
            </Card>
          )}
        </Flex>
      );
    });
  }

  return (
    <Card p={1}>
      <Flex gap={20}>
        <Heading size="jumbo">Multi-Book Search</Heading>
        <Text
          copy
          size="base"
          pb={1}
          color="copyDark"
        >{`Buying multiple books from the same Pango seller is the best way to save! Use this page to find sellers with multiple books on your reading list and build your perfect book bundle.`}</Text>
        <Text
          copy
          size="base"
          color="copyDark"
        >{`Prefill with a popular series (optional)`}</Text>
        <Box style={{ width: '100%' }}>
          <PopularSeries
            onSelectSeries={handleSelectSeries}
            series={series}
            selectedSeries={selectedSeries}
          />
        </Box>
        <Box mt={1} style={{ width: '100%' }}>
          <Flex gap={20} style={{ width: '100%' }}>
            <Text size="base">Search by title</Text>
            {renderInputs()}
            <Button
              type="button"
              onPress={addInputBox}
              text="Add Another Book"
              style="tertiary"
              size="base"
              icon={<PlusSVG />}
            />
            <Flex style={{ alignSelf: 'flex-end' }}>
              <Flex direction="row" gap={10}>
                <Button
                  text="Clear"
                  style="secondary"
                  size="base"
                  onPress={handleClear}
                  type="button"
                />
                <Button
                  type="button"
                  text="Search"
                  style="primary"
                  size="base"
                  onPress={handleSearch}
                />
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Card>
  );
}
